/*
 * Lemonade Training Oy, copy rights, all right reserved.
 * Turns a change in the own-Users grant fields into exactly one forced token
 * refresh, so a role change reaches the dashboard without a reload.
 */

import { useEffect, useRef } from "react";
import {
  firebaseAuth,
  refreshClaims,
  useAppDispatch,
  useAppSelector,
} from "@lemonadetraining/web-shared";
import { grantSignalEquals } from "@lemonadetraining/web-shared/store/grantSignal";
import type { GrantSignal } from "@lemonadetraining/web-shared";
import { writeAccessEndedMarker } from "@lemonadetraining/web-shared/utils/accessEndedMarker";

/** Long enough for the post-commit claim mint to land, short enough to feel live. */
const DEBOUNCE_MS = 2000;

/**
 * How long the refresh waits for a removal notice that may never come. Past
 * this the person is signed out un-told, which is worse than a stale tab.
 */
const REMOVAL_GRACE_MS = 8000;

export { ACCESS_ENDED_MARKER_KEY } from "@lemonadetraining/web-shared/utils/accessEndedMarker";

/**
 * The one transition after which the server revokes refresh tokens: a staff
 * role gone AND no membership left. A demotion that keeps either is not one.
 */
const isRemovalTransition = (prev: GrantSignal, next: GrantSignal): boolean =>
  (prev.boxRole === "admin" || prev.boxRole === "coach") &&
  next.boxRole === null &&
  next.boxID === null;

export function useGrantRefresh(): void {
  const dispatch = useAppDispatch();
  const uid = useAppSelector((state) => state.user.user?.uid ?? null);
  const grantSignal = useAppSelector((state) => state.user.grantSignal);
  const unread = useAppSelector((state) => state.accessNotices.unread);

  const prevRef = useRef<GrantSignal | undefined>(undefined);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const inFlightRef = useRef(false);
  const pendingRef = useRef(false);
  const holdingRef = useRef(false);
  const sawRemovalNoticeRef = useRef(false);
  const gymNameRef = useRef<string | null>(null);
  const timerControlRef = useRef<{ schedule: (delay: number) => void; clear: () => void }>({
    schedule: () => {},
    clear: () => {},
  });

  const removalNotice = unread.find(
    (notice: { kind?: string }) => notice.kind === "removed_from_staff"
  );
  const pendingRemovalNotice = Boolean(removalNotice);
  if (removalNotice?.gymName) gymNameRef.current = removalNotice.gymName;
  if (pendingRemovalNotice) sawRemovalNoticeRef.current = true;

  // Deliberately keyed on the grant alone. Notices arrive on their own cadence,
  // and a notice landing mid-debounce must not tear down the pending refresh.
  useEffect(() => {
    const clearTimer = () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };

    const run = async () => {
      timerRef.current = null;
      if (inFlightRef.current) {
        pendingRef.current = true;
        return;
      }
      const user = firebaseAuth.currentUser;
      if (!user) return;

      inFlightRef.current = true;
      try {
        await refreshClaims(user, dispatch);
      } catch (error) {
        const code = (error as { code?: string } | null)?.code;
        if (code === "auth/user-token-expired" || code === "auth/user-disabled") {
          // Not a failed read: the gym access this token carried is gone.
          writeAccessEndedMarker(gymNameRef.current);
        } else {
          console.warn("Grant refresh failed:", code ?? "unknown");
        }
      } finally {
        inFlightRef.current = false;
        if (pendingRef.current) {
          pendingRef.current = false;
          schedule(DEBOUNCE_MS);
        }
      }
    };

    const schedule = (delay: number) => {
      clearTimer();
      timerRef.current = setTimeout(() => {
        void run();
      }, delay);
    };
    timerControlRef.current = { schedule, clear: clearTimer };

    const decide = () => {
      if (!uid) {
        prevRef.current = undefined;
        holdingRef.current = false;
        sawRemovalNoticeRef.current = false;
        return;
      }
      if (!grantSignal) return;

      if (prevRef.current === undefined) {
        // Seed only: the cold-start path has already minted a fresh token.
        prevRef.current = grantSignal;
        return;
      }
      if (grantSignalEquals(prevRef.current, grantSignal)) return;

      const previous = prevRef.current;
      prevRef.current = grantSignal;

      if (isRemovalTransition(previous, grantSignal)) {
        // The server writes the notice and then revokes the token. Refreshing
        // first would sign the person out before they are told why.
        writeAccessEndedMarker(gymNameRef.current);
        holdingRef.current = true;
        // The two listeners are independent, so the notice can land before or
        // after this. Arm the grace only when it has not arrived yet; if it
        // has, acknowledgement is what releases the hold and a timer here
        // would pull the modal away mid-read.
        if (!pendingRemovalNotice) schedule(REMOVAL_GRACE_MS);
        return;
      }

      schedule(DEBOUNCE_MS);
    };

    decide();
    return clearTimer;
  }, [uid, grantSignal, dispatch]);

  /*
   * The grace timer only ever covers the case where the notice never arrives.
   * Once it does arrive the timer is cancelled and the refresh waits for the
   * person to acknowledge it — otherwise they are signed out mid-read, which
   * is the very thing the hold exists to prevent.
   */
  useEffect(() => {
    if (!uid || !holdingRef.current) return;
    if (pendingRemovalNotice) {
      timerControlRef.current.clear();
      return;
    }
    if (!sawRemovalNoticeRef.current) return;
    holdingRef.current = false;
    timerControlRef.current.schedule(0);
  }, [uid, pendingRemovalNotice]);
}
